/**
 * The inventory item form (EW-9) — new and edit, a full-width panel, never a
 * modal. Prices are in the company's currency (the API stamps it). The unit is
 * fixed once the item has stock history (409 `unit_has_history`).
 * A blank price, threshold or location is sent as null: absent, never 0.
 */
import React, { useState } from 'react';
import { blankToNull, toInput } from '../../../lib/earthworks';
import type { EwInventoryOptions, EwMaterial, EwMaterialLocation, EwMaterialWrite } from '../../../types/earthworks';
import { EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwField, EwFormError } from '../EwBits';

const AMOUNT = /^\d+(\.\d{1,2})?$/;
const QTY = /^\d+(\.\d{1,3})?$/;

export function EwMaterialForm({
  material,
  options,
  locations,
  currency,
  unitLocked,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  material: EwMaterial | null;
  options: EwInventoryOptions | undefined;
  locations: EwMaterialLocation[];
  currency: string | null;
  unitLocked: boolean;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: EwMaterialWrite) => void;
}) {
  const [f, setF] = useState({
    name: material?.name ?? '',
    code: material?.code ?? '',
    category: material?.category ?? 'other',
    unit: material?.unit ?? '',
    cost: toInput(material?.costPerUnit),
    sell: toInput(material?.sellPrice),
    rateMaterial: material?.rateMaterial ?? '',
    locationId: material?.locationId ?? '',
    threshold: toInput(material?.lowStockThreshold),
    notes: material?.notes ?? '',
  });
  const [localError, setLocalError] = useState<string | null>(null);
  const set = (patch: Partial<typeof f>) => setF((cur) => ({ ...cur, ...patch }));
  const money = currency ? ` (${currency})` : '';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!f.name.trim()) return setLocalError('Enter the item name.');
    if (!f.unit) return setLocalError('Choose the unit the item is stocked in.');
    for (const [label, v] of [['The cost per unit', f.cost], ['The sell price', f.sell]] as const) {
      if (v.trim() && !AMOUNT.test(v.trim())) return setLocalError(`${label} must be an amount, like 28 or 28.50.`);
    }
    if (f.threshold.trim() && !QTY.test(f.threshold.trim())) return setLocalError('The reorder point must be a quantity of 0 or more.');
    onSubmit({
      name: f.name.trim(),
      code: blankToNull(f.code),
      category: f.category,
      ...(unitLocked ? {} : { unit: f.unit }),
      cost_per_unit: blankToNull(f.cost),
      sell_price: blankToNull(f.sell),
      rate_material: f.rateMaterial || null,
      location_id: f.locationId || null,
      low_stock_threshold: blankToNull(f.threshold),
      notes: blankToNull(f.notes),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="ew-material-form">
      <EwFormError message={localError ?? error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <EwField label="Name">
          <input className={EW_INPUT} aria-label="Item name" value={f.name} onChange={(e) => set({ name: e.target.value })} />
        </EwField>
        <EwField label="Code" hint="Optional">
          <input className={EW_INPUT} aria-label="Item code" value={f.code} onChange={(e) => set({ code: e.target.value })} />
        </EwField>
        <EwField label="Category">
          <select className={EW_INPUT} aria-label="Category" value={f.category} onChange={(e) => set({ category: e.target.value })}>
            {(options?.categories ?? []).map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </EwField>
        <EwField label="Unit" hint={unitLocked ? 'Fixed — the item has stock history' : undefined}>
          <select className={EW_INPUT} aria-label="Unit" value={f.unit} disabled={unitLocked} onChange={(e) => set({ unit: e.target.value })}>
            <option value="">Choose…</option>
            {(options?.units ?? []).map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </EwField>
        <EwField label={`Cost per unit${money}`} hint="Optional">
          <input className={EW_INPUT} inputMode="decimal" aria-label="Cost per unit" value={f.cost} onChange={(e) => set({ cost: e.target.value })} />
        </EwField>
        <EwField label={`Sell price${money}`} hint="Bills a ticket when no rate is bound">
          <input className={EW_INPUT} inputMode="decimal" aria-label="Sell price" value={f.sell} onChange={(e) => set({ sell: e.target.value })} />
        </EwField>
        <EwField label="Rate-card material" hint="A material rate for it prices tickets first">
          <select className={EW_INPUT} aria-label="Rate-card material" value={f.rateMaterial} onChange={(e) => set({ rateMaterial: e.target.value })}>
            <option value="">None</option>
            {(options?.rateMaterials ?? []).map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </EwField>
        <EwField label="Home location" hint="Where it is kept by default">
          <select className={EW_INPUT} aria-label="Home location" value={f.locationId} onChange={(e) => set({ locationId: e.target.value })}>
            <option value="">None</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </EwField>
        <EwField label="Reorder at" hint="Warns at or below this; blank = never">
          <input className={EW_INPUT} inputMode="decimal" aria-label="Reorder at" value={f.threshold} onChange={(e) => set({ threshold: e.target.value })} />
        </EwField>
      </div>
      <EwField label="Notes" hint="Optional">
        <textarea className={`${EW_INPUT} min-h-[4rem]`} aria-label="Notes" value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
      </EwField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>Cancel</button>
        <button type="submit" className={EW_PRIMARY} disabled={pending}>{pending ? 'Saving…' : submitLabel}</button>
      </div>
    </form>
  );
}
